import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  updateIssueStatus,
  assignIssueToUser,
  deleteIssue,
  getIssueAssignedUsers,
} from '../store/slices/issueSlice';
import SweetAlert from '../utils/sweetAlert';

export const useIssueActions = (issuesData, permissions = {}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    canChangeIssueStatus = true,
    canAssignIssue = true,
    canDeleteIssue = true,
    canViewListIssue = true,
  } = permissions;

  const [dialogs, setDialogs] = useState({
    status: false,
    assign: false,
    assignedUsers: false,
  });
  const [statusData, setStatusData] = useState({ newStatus: '', description: '' });
  const [assignData, setAssignData] = useState({ selectedUser: '' });
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [assignedUsersLoading, setAssignedUsersLoading] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);

  const handleViewIssue = (id) => navigate(`/issues/view/${id}`);
  const handleEditIssue = (id) => navigate(`/issues/edit/${id}`);

  const openDialog = (name) => setDialogs((p) => ({ ...p, [name]: true }));
  const closeDialog = (name) => setDialogs((p) => ({ ...p, [name]: false }));

  const handleOpenStatusDialog = (issue) => {
    setSelectedIssue(issue);
    setStatusData({ newStatus: issue?.last_issue_log?.status?.id || '', description: '' });
    openDialog('status');
  };

  const handleCloseStatusDialog = () => {
    closeDialog('status');
    setStatusData({ newStatus: '', description: '' });
  };

  const handleOpenAssignDialog = (issue) => {
    setSelectedIssue(issue);
    setAssignData({ selectedUser: '' });
    openDialog('assign');
  };

  const handleCloseAssignDialog = () => {
    closeDialog('assign');
    setAssignData({ selectedUser: '' });
  };

  const handleOpenAssignedUsersDialog = (issue) => {
    setSelectedIssue(issue);
    openDialog('assignedUsers');
    fetchAssignedUsers(issue.id);
  };

  const handleCloseAssignedUsersDialog = () => {
    closeDialog('assignedUsers');
    setAssignedUsers([]);
  };

  const handleStatusChange = async () => {
    if (!canChangeIssueStatus) {
      toast.error('You do not have permission to change issue status.');
      return;
    }
    if (!statusData.newStatus) {
      toast.warn('Select a status');
      return;
    }
    try {
      issuesData.setProcessingId(selectedIssue.id);
      const payload = { status_id: statusData.newStatus };
      if (statusData.description?.trim()) payload.description = statusData.description.trim();
      const res = await dispatch(
        updateIssueStatus({
          issueId: selectedIssue.id,
          statusData: payload,
        })
      ).unwrap();
      if (res?.success) {
        issuesData.fetchIssues();
        toast.success('Status updated');
        handleCloseStatusDialog();
      }
    } catch (err) {
      toast.error(err?.message || err || 'Failed to update status');
    } finally {
      issuesData.setProcessingId(null);
    }
  };

  const handleAssign = async () => {
    if (!canAssignIssue) {
      toast.error('You do not have permission to assign issues.');
      return;
    }
    if (!assignData.selectedUser) {
      toast.warn('Select a user');
      return;
    }
    try {
      issuesData.setProcessingId(selectedIssue.id);
      const res = await dispatch(
        assignIssueToUser({
          issueId: selectedIssue.id,
          userId: assignData.selectedUser,
        })
      ).unwrap();
      if (res?.success) {
        const user = issuesData.usersList.find((u) => u.id === assignData.selectedUser);
        issuesData.setIssues((prev) =>
          prev.map((issue) =>
            issue.id === selectedIssue.id ? { ...issue, assignee: user } : issue
          )
        );
        toast.success('Issue assigned');
        handleCloseAssignDialog();
      }
    } catch (err) {
      toast.error(err?.message || err || 'Failed to assign issue');
    } finally {
      issuesData.setProcessingId(null);
    }
  };

  const handleDeleteIssue = async (issue) => {
    if (!canDeleteIssue) {
      toast.error('You do not have permission to delete issues.');
      return;
    }
    try {
      const result = await SweetAlert.deleteConfirm(issue.title || 'this issue');
      if (!result.isConfirmed) return;

      issuesData.setProcessingId(issue.id);
      await dispatch(deleteIssue(issue.id)).unwrap();
      await SweetAlert.success({ text: 'Issue deleted successfully' });
      issuesData.fetchIssues();
    } catch (error) {
      console.error('Delete issue error:', error);
      await SweetAlert.error({ text: 'Failed to delete issue' });
    } finally {
      issuesData.setProcessingId(null);
    }
  };

  const fetchAssignedUsers = async (issueId) => {
    if (!issueId) return;
    try {
      setAssignedUsersLoading(true);
      const res = await dispatch(getIssueAssignedUsers(issueId)).unwrap();
      const list = res?.data?.issue_logs ?? res?.issue_logs ?? [];
      setAssignedUsers(list);
    } catch (err) {
      toast.error(err?.message || 'Failed to load assigned users');
      handleCloseAssignedUsersDialog();
    } finally {
      setAssignedUsersLoading(false);
    }
  };

  const setNewStatus = (status) => setStatusData((p) => ({ ...p, newStatus: status }));
  const setStatusDescription = (description) => setStatusData((p) => ({ ...p, description: description ?? '' }));
  const setSelectedUser = (user) => setAssignData((p) => ({ ...p, selectedUser: user }));

  return {
    dialogs,
    statusData,
    assignData,
    assignedUsers,
    assignedUsersLoading,
    selectedIssue,
    handleViewIssue,
    handleEditIssue,
    handleOpenStatusDialog,
    handleCloseStatusDialog,
    handleOpenAssignDialog,
    handleCloseAssignDialog,
    handleOpenAssignedUsersDialog,
    handleCloseAssignedUsersDialog,
    handleStatusChange,
    handleAssign,
    handleDeleteIssue,
    setNewStatus,
    setStatusDescription,
    setSelectedUser,
    setSelectedIssue,
  };
};
